import React, { useState, useEffect } from "react";
import classNames from "classnames/bind";
import styles from "../../AddJobModal.module.scss";
import BaseAddressInput from "~/components/common/base-address-input/BaseAddressInput";
import {
  BillingAddressType,
  AddressSuggestion,
} from "../../types/add-job.types";

const cx = classNames.bind(styles);

interface BillingAddressSectionProps {
  jobAddress: string;
  customerAddress?: string;
  initialBillingAddress?: string;
  initialBillingAddressType?: BillingAddressType;
  initialFlagSameAsJobAddress?: boolean;
  initialFlagSameAsCustomerAddress?: boolean;
  onBillingAddressChange: (
    address: string,
    source?: string,
    flagSameAsJobAddress?: boolean,
    flagSameAsCustomerAddress?: boolean,
    billingAddressType?: BillingAddressType
  ) => void;
  onBillingAddressSuggestionSelect?: (suggestion: AddressSuggestion) => void;
}

const BillingAddressSection: React.FC<BillingAddressSectionProps> = ({
  jobAddress,
  customerAddress,
  initialBillingAddress = "",
  initialBillingAddressType = "existing",
  initialFlagSameAsJobAddress = false,
  initialFlagSameAsCustomerAddress = false,
  onBillingAddressChange,
  onBillingAddressSuggestionSelect,
}) => {
  const [billingAddressType, setBillingAddressType] =
    useState<BillingAddressType>(initialBillingAddressType);
  const [billingAddress, setBillingAddress] = useState<string>(
    initialBillingAddress
  );
  const [sameAsJobAddress, setSameAsJobAddress] = useState<boolean>(
    initialFlagSameAsJobAddress
  );
  const [sameAsCustomerAddress, setSameAsCustomerAddress] = useState<boolean>(
    initialFlagSameAsCustomerAddress
  );

  useEffect(() => {
    setBillingAddress(initialBillingAddress);
    setBillingAddressType(initialBillingAddressType);
    setSameAsJobAddress(initialFlagSameAsJobAddress);
    setSameAsCustomerAddress(initialFlagSameAsCustomerAddress);
  }, [
    initialBillingAddress,
    initialBillingAddressType,
    initialFlagSameAsJobAddress,
    initialFlagSameAsCustomerAddress,
  ]);

  const handleBillingAddressTypeChange = (type: BillingAddressType) => {
    setBillingAddressType(type);
    setBillingAddress("");
    setSameAsJobAddress(false);
    setSameAsCustomerAddress(false);
    onBillingAddressChange("", "", false, false, type);
  };

  const handleSameAsJobAddressChange = (checked: boolean) => {
    setSameAsJobAddress(checked);
    if (checked) {
      setSameAsCustomerAddress(false);
      setBillingAddress(jobAddress);
      onBillingAddressChange(
        jobAddress,
        "Job Address",
        true,
        false,
        billingAddressType
      );
    } else {
      setBillingAddress("");
      onBillingAddressChange("", "", false, false, billingAddressType);
    }
  };

  const handleSameAsCustomerAddressChange = (checked: boolean) => {
    setSameAsCustomerAddress(checked);
    if (checked) {
      setSameAsJobAddress(false);
      if (customerAddress) {
        setBillingAddress(customerAddress);
        onBillingAddressChange(
          customerAddress,
          "Customer Address",
          false,
          true,
          billingAddressType
        );
      }
    } else {
      setBillingAddress("");
      onBillingAddressChange("", "", false, false, billingAddressType);
    }
  };

  const handleNewAddressChange = (value: string) => {
    setBillingAddress(value);
    setSameAsJobAddress(false);
    setSameAsCustomerAddress(false);
    onBillingAddressChange(
      value,
      "New Address",
      false,
      false,
      billingAddressType
    );
  };

  return (
    <div className={cx("billing-address")}>
      <h4 className={cx("title")}>Billing Address</h4>
      <div className={cx("actions")}>
        <button
          className={cx("button", {
            active: billingAddressType === "existing",
          })}
          onClick={() => handleBillingAddressTypeChange("existing")}
        >
          Use Existing
        </button>
        <button
          className={cx("button", {
            active: billingAddressType === "new",
          })}
          onClick={() => handleBillingAddressTypeChange("new")}
        >
          Add New
        </button>
      </div>

      {billingAddressType === "existing" ? (
        <div className={cx("existing-address")}>
          <div className={cx("checkbox-container")}>
            <input
              type="checkbox"
              checked={sameAsJobAddress}
              onChange={(e) => handleSameAsJobAddressChange(e.target.checked)}
              className={cx("checkbox")}
            />
            <label className={cx("checkbox-label")}>Same as Job Address</label>
          </div>
          <div className={cx("checkbox-container")}>
            <input
              type="checkbox"
              checked={sameAsCustomerAddress}
              onChange={(e) =>
                handleSameAsCustomerAddressChange(e.target.checked)
              }
              disabled={!customerAddress}
              className={cx("checkbox")}
            />
            <label className={cx("checkbox-label")}>
              Same as Customer Address
            </label>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: "16px" }}>
          <BaseAddressInput
            value={billingAddress}
            onChange={handleNewAddressChange}
            onSuggestionSelect={onBillingAddressSuggestionSelect}
            placeholder="Enter billing address"
            label=""
            className={cx("billing-address-input")}
          />
        </div>
      )}
    </div>
  );
};

export default BillingAddressSection;
